import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useUser } from '@/hooks/useUser';
import { useSnackbar } from '@/features/snackbar';
import { getQueryKey as getNarrativePermissionQueryKey } from '../useNarrativePermission';
import type { NarrativePermission } from '../useNarrativePermission';
import { mutationFn } from './mutationFn';
import { useNarrative } from '../useNarrative';
import type { ResolvedQueryData } from '@/utilities/genericTypes';

export const useVoteOnNarrative = (slug: string, isUpVoted: boolean) => {
  const { data: user } = useUser();
  const queryClient = useQueryClient();
  const { addSnackbar } = useSnackbar();

  return useMutation({
    mutationFn,
    onSuccess: (response, { narrativeId }) => {
      queryClient.setQueryData<ResolvedQueryData<typeof useNarrative>>(
        useNarrative.getOptions({ slug }).queryKey,
        (state) => {
          if (typeof state === 'undefined') return state;
          return {
            narrative: {
              ...state.narrative,
              voteCount: {
                upvotes:
                  response.narrative?.voteCount.upvotes ??
                  state.narrative.voteCount.upvotes,
              },
            },
          };
        }
      );

      queryClient.setQueryData<NarrativePermission>(
        getNarrativePermissionQueryKey(user.id),
        (state) => {
          if (typeof state !== 'undefined' && state.userVotedNarratives) {
            return {
              ...state,
              userVotedNarratives: isUpVoted
                ? state.userVotedNarratives.filter(
                    (n) => n.narrativeId !== narrativeId
                  )
                : [...state.userVotedNarratives, { narrativeId }],
            };
          }
          return state;
        }
      );
    },
    onError: () => {
      addSnackbar({
        type: 'negative',
        lifeTime: 2000,
        message: `Failed to ${isUpVoted ? 'down' : 'up'}voted narrative`,
      });
    },
  });
};
